// Billing / subscription API.
// Mirrors the axios+token pattern used by api/onboarding.ts and api/progress.ts
// (token in localStorage 'cleveroo_token', base from REACT_APP_API_URL).

import axios from 'axios';
import {
  Plan,
  SubscriptionStatus,
  CheckoutSessionResponse,
  PortalSessionResponse,
  BillingAccountResponse,
  BillingMember,
  API_ENDPOINTS,
} from '../types/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

function authHeaders(): Record<string, string> {
  const token =
    localStorage.getItem('cleveroo_token') ||
    localStorage.getItem('accessToken') ||
    '';
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Fetch all available subscription plans (public — no auth required). */
export async function getPlans(): Promise<Plan[]> {
  const res = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.BILLING.PLANS}`, {
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
  });
  return res.data?.data ?? [];
}

/** Fetch the authenticated user's current subscription status. */
export async function getSubscription(): Promise<SubscriptionStatus | null> {
  const res = await axios.get(
    `${API_BASE_URL}${API_ENDPOINTS.BILLING.SUBSCRIPTION}`,
    {
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
    }
  );
  return res.data?.data ?? null;
}

/**
 * Start a Stripe checkout session for the given price ID.
 * Redirects the browser to the Stripe-hosted checkout page on success.
 */
export async function startCheckout(priceId: string): Promise<void> {
  const successUrl = `${window.location.origin}/billing/success`;
  const cancelUrl = `${window.location.origin}/billing/cancel`;

  const res = await axios.post(
    `${API_BASE_URL}${API_ENDPOINTS.BILLING.CHECKOUT_SESSION}`,
    { priceId, successUrl, cancelUrl },
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );

  const data: CheckoutSessionResponse = res.data?.data;
  if (data?.url) {
    window.location.assign(data.url);
  } else {
    throw new Error('No checkout URL returned from server');
  }
}

/**
 * Open the Stripe billing portal so the customer can manage their subscription.
 * Redirects the browser to the Stripe-hosted portal page on success.
 */
export async function openBillingPortal(): Promise<void> {
  const res = await axios.post(
    `${API_BASE_URL}${API_ENDPOINTS.BILLING.PORTAL_SESSION}`,
    {},
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );

  const data: PortalSessionResponse = res.data?.data;
  if (data?.url) {
    window.location.assign(data.url);
  } else {
    throw new Error('No portal URL returned from server');
  }
}

// ─── Family / billing account ─────────────────────────────────────────────────

/**
 * Fetch the authenticated user's billing account and its members.
 * Returns null when the user has no billing account yet.
 */
export async function getAccount(): Promise<BillingAccountResponse | null> {
  const res = await axios.get(`${API_BASE_URL}/api/billing/account`, {
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
  });
  return res.data?.data ?? null;
}

/**
 * Add a child member to the current user's billing account by email.
 * The server returns the new member object on success.
 * On failure the server body contains an `error` string — we throw that message.
 */
export async function addMember(email: string): Promise<BillingMember> {
  const res = await axios.post(
    `${API_BASE_URL}/api/billing/members`,
    { email },
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );
  return res.data?.data as BillingMember;
}

/**
 * Remove a child member from the current user's billing account.
 */
export async function removeMember(userId: string): Promise<void> {
  await axios.delete(`${API_BASE_URL}/api/billing/members/${userId}`, {
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
  });
}
